import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { useToast } from './Toast';
import { useConfirm } from './Modal';
import { PUZZLE_MATERIAL_SLOTS } from './PuzzleMaterialView';
import { Image, Upload, Trash2, Loader } from 'lucide-react';

// 后台 · 获客素材（证券 4 张步骤图）管理：逐张上传/替换/删除。
// 后端 JSON 请求体上限 2MB，所以过大的图先在浏览器里压成 JPEG 再传。
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('读取图片失败'));
    reader.readAsDataURL(file);
  });
}
async function shrinkIfNeeded(file) {
  const raw = await fileToDataUrl(file);
  if (raw.length < 1.5 * 1024 * 1024) return raw;   // 够小就原样上传（保持 PNG 清晰度）
  const img = await new Promise((resolve, reject) => {
    const i = new window.Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('图片格式不支持'));
    i.src = raw;
  });
  for (const [edge, q] of [[1800, 0.86], [1400, 0.8], [1100, 0.72]]) {
    const scale = Math.min(1, edge / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    const out = c.toDataURL('image/jpeg', q);
    if (out.length < 1.6 * 1024 * 1024) return out;
  }
  throw new Error('图片太大，请换一张小一点的');
}

export default function PuzzleMaterialManage() {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyNo, setBusyNo] = useState(null);
  const inputRef = useRef(null);
  const pickNo = useRef(null);

  const fetchAll = useCallback(() => {
    return api.get('/puzzle/materials').then(r => setImages(r.data.images || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const choose = (no) => { pickNo.current = no; inputRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const no = pickNo.current;
    if (!file || !no) return;
    if (!file.type.startsWith('image/')) { showToast('请选择图片文件', 'error'); return; }
    setBusyNo(no);
    try {
      const imageData = await shrinkIfNeeded(file);
      const res = await api.post(`/puzzle/material/${no}`, { imageData });
      showToast(res.data.message || '保存成功', 'success');
      await fetchAll();
    } catch (err) {
      showToast(err.response?.data?.error || err.message || '上传失败', 'error');
    } finally { setBusyNo(null); }
  };
  const remove = async (no) => {
    const ok = await confirm(`确认删除第 ${no} 步素材图？`, { detail: '删除后不可恢复。', danger: true });
    if (!ok) return;
    setBusyNo(no);
    try {
      await api.delete(`/puzzle/material/${no}`);
      showToast('已删除', 'success');
      await fetchAll();
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    } finally { setBusyNo(null); }
  };

  if (loading) return <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>;
  const byNo = Object.fromEntries(images.map(i => [i.slot_no, i]));

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
      <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', lineHeight: 1.6, margin: '0 2px 10px' }}>
        证券业务固定 4 张步骤图（对应获客的 4 个步骤），用户端“获客素材 → 证券”里逐张展示并可保存。
      </div>
      {PUZZLE_MATERIAL_SLOTS.map(no => {
        const img = byNo[no];
        const busy = busyNo === no;
        return (
          <div key={no} className="card" style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, textAlign: 'left' }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--c-primary)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{no}</span>
              <span style={{ fontSize: 15, fontWeight: 700, flex: 1 }}>第 {no} 步素材图</span>
              <span className={`badge ${img ? 'badge-success' : 'badge-warning'}`}>{img ? '已设置' : '未设置'}</span>
            </div>
            {img ? (
              <img src={img.image_data} alt={`步骤图${no}`} style={{ display: 'block', margin: '0 auto 12px', maxWidth: '100%', maxHeight: 240, borderRadius: 10 }} />
            ) : (
              <div style={{ padding: '18px 0', color: 'var(--c-text-tertiary)' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}><Image size={32} strokeWidth={1.2} /></div>
                <span style={{ fontSize: 12 }}>暂无图片</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline" onClick={() => choose(no)} disabled={busy} style={{ height: 42, flex: 1 }}>
                {busy ? <Loader size={15} className="spin" /> : <Upload size={15} />} {img ? '替换' : '上传'}
              </button>
              {img && (
                <button className="btn btn-ghost" onClick={() => remove(no)} disabled={busy}
                  style={{ height: 42, width: 'auto', padding: '0 16px', border: '1px solid var(--c-border)', color: 'var(--c-danger)' }}>
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
